/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';

export const DEFAULT_TIMEOUT = 5000;

export enum SiteAlertColors {
  Primary = 'primary',
  Secondary = 'secondary',
  Success = 'success',
  Danger = 'danger',
  Warning = 'warning',
  Info = 'info',
  Light = 'light',
  Dark = 'dark'
}

export interface SiteAlert {
  content: string | JSX.Element;
  color?: SiteAlertColors;
  timeout?: number;
  id?: number;
}

interface SiteAlertsInitialState {
  alerts: SiteAlert[];
  autoIncrementId: number;
}

const getInitialState = (): SiteAlertsInitialState => {
  return {
    alerts: [],
    autoIncrementId: 1000000 // Just so it isn't a tiny number to start...
  }
}

const siteAlerts = createSlice({
  name: 'siteAlerts',
  initialState: getInitialState(),
  reducers: {
    add: (state, action: PayloadAction<SiteAlert>) => {
      const newAlert = {
        ...(action.payload.color === SiteAlertColors.Danger && { timeout: DEFAULT_TIMEOUT }),
        ...action.payload,
        id: state.autoIncrementId
      };

      return {
        alerts: [...state.alerts, newAlert ],
        autoIncrementId: state.autoIncrementId + 1
      }
    },
    remove: (state, action: PayloadAction<SiteAlert>) => {
      return {
        ...state,
        alerts: state.alerts.filter(a => a.id !== action.payload.id)
      }
    },
    clear: () => getInitialState()
  }
})

const { add, remove } = siteAlerts.actions;


type ErrorTypes = Pick<SiteAlert, 'content'> | Error | string;
const getMessage = (error: ErrorTypes) => {
  if (typeof error === 'string') {
    return error;
  }
  return 'content' in error ? error.content : error.message;
}

export const addSiteError = (alert: Pick<SiteAlert, 'content'> | Error | string) => {
  return (dispatch: Dispatch) => dispatch(add({
    color: SiteAlertColors.Danger,
    content: getMessage(alert),
  }));
};

export const addSiteAlert = (alert: SiteAlert) => (dispatch: Dispatch) => dispatch(add(alert));

export const removeSiteAlert = (alert: SiteAlert) => (dispatch: Dispatch) => dispatch(remove(alert));

export default siteAlerts.reducer;

